import './SavedForecasts.scss';
import cn from 'classnames';
import {
  CSSProperties,
  Dispatch,
  MouseEventHandler,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { MdAdd, MdContentCopy, MdDelete, MdMoreVert, MdOpenInNew } from 'react-icons/md';
import {
  Forecast,
  ForecastGroup,
  ForecastId,
  ForecastStatus
} from '@blisspointmedia/bpm-types/dist/Forecasting';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { AlertDialog } from '@Components/AlertDialog';
import { SegmentedControlButton } from '@src/Components/SegmentedControlButton';
import { Button } from '../../../Components/Button/Button';
import { Appearance, Design, Intent, Size } from '../../../Components/designConstants';
import { FilterBar } from '../../../Components/FilterBar';
import { IconButton } from '../../../Components/IconButton';
import { FullPageSpinner } from '../../../Components/Spinner';
import { BPMTable, SuperTopItemType, TypedHeader } from '../../../Components/StickyTable';
import { TableWidget } from '../../../Components/TableWidget/TableWidget';
import { ToggleSwitch } from '../../../Components/ToggleSwitch';
import { SetError } from '../../../redux/modals';
import { dateToMddyy } from '../../../utils/date-utils';
import { GoToTab } from '../../../utils/hooks/useNav';
import {
  BudgetViewOptions,
  formatRawBudget,
  PageTab,
  PageTabValue
} from '../forecasting-constants';
import {
  ALL_PLATFORMS_LABEL,
  ALL_TACTICS_LABEL,
  isLastVisible,
  joinDateRange,
  pooledPlatformLabel,
  pooledTacticLabel
} from '../forecasting-utils';
import { StatusChip } from './StatusChip';

interface SavedForecastsProps {
  cid: string;
  forecastGroups: ForecastGroup[] | undefined;
  budgetView: BudgetViewOptions;
  setBudgetView: Dispatch<SetStateAction<BudgetViewOptions>>;
  setError: SetError;
  canAddExistingForecast: boolean;
  onDeleteForecast: (forecastId: ForecastId) => Promise<void>;
  showWIPFeatures?: boolean;
  goToTab: GoToTab<PageTabValue>;
}

interface ForecastToDelete {
  forecastId: ForecastId;
  startDate: string;
  endDate: string;
  lastModifiedDate: string;
}
type ForecastRow = Omit<ForecastGroup, 'forecasts'> & Forecast;

const BASE_ROW_HEIGHT = 50;
const SM_COL_WIDTH = 100;
const MED_COL_WIDTH = 150;
const OUTER_X_PADDING = 16;
const INNER_X_PADDING = 24;
const FILTER_BAR_WIDTH = 600;
interface SuperHeaderData {
  label: string;
}
const leftSuperHeaders: SuperTopItemType<SuperHeaderData>[] = [
  { span: 2, data: { label: 'Forecast Parameters' } }
];
const rightSuperHeaders: SuperTopItemType<SuperHeaderData>[] = [
  { span: 7, data: { label: 'Channels and Budgets' } },
  { span: 2, data: { label: 'Details' } }
];
const EMPTY_KPI_TEXT = 'None';

interface LeftData {
  groupId: string;
  startDate: string;
  endDate: string;
  status: ForecastStatus;
  groupForecastIds: string[];
}

interface RightData extends LeftData {
  forecastId: string;
  channel: string;
  platform: string;
  tactic: string;
  volumeKpis: string;
  revenueKpis: string;
  primaryBudget: string;
  comparisonBudgets: string;
  lastModifiedUser: string;
  lastModifiedDate: string;
  isClientFacing: boolean;
}

const filterBarOptions = [
  'groupId',
  'startDate',
  'endDate',
  'volumeKpis',
  'revenueKpis',
  'status',
  'forecastId',
  'channel',
  'platform',
  'tactic',
  'primaryBudget',
  'comparisonBudgets',
  'lastModifiedUser',
  'lastModifiedDate',
  'isClientFacing'
];

// Matches superHeadersDefaultRenderer, except adds bpm-super-header-inner wrapper
const noRowsRenderer = () => <div>No rows to show.</div>;

const superHeadersRenderer = ({
  data,
  style,
  classes
}: {
  data: SuperHeaderData;
  style: CSSProperties;
  classes: string[];
}): ReactElement => (
  <div
    style={style}
    className={cn(classes, 'bpm-super-header', {
      'bpm-super-header-with-data': Boolean(data.label)
    })}
  >
    <div className='bpm-super-header-inner'>{data.label}</div>
  </div>
);

export const SavedForecasts = ({
  cid,
  forecastGroups,
  budgetView,
  setBudgetView,
  setError,
  canAddExistingForecast,
  onDeleteForecast,
  showWIPFeatures = false,
  goToTab
}: SavedForecastsProps): ReactElement => {
  const canDeleteForecasts = !TEST_COMPANIES.includes(cid);

  const doubleTableRef = useRef<HTMLDivElement>(null);
  const [doubleTableWidth, setDoubleTableWidth] = useState<number | null>(null);
  const [leftTableWidth, setLeftTableWidth] = useState<number | null>(null);
  const [resizeActive, setResizeActive] = useState(false);
  const [filter, setFilter] = useState<
    { filter: (line: Omit<RightData, 'groupForecastIds'>) => boolean } | undefined
  >();
  const [forecastToDelete, setForecastToDelete] = useState<ForecastToDelete | null>(null);
  // Mirrors forecastToDelete while a row is targeted, but is only cleared on the dialog's
  // onExited (after its fade-out finishes) rather than immediately on hide -- so the body still
  // has a summary to render while the (now-nulled) forecastToDelete would otherwise blank it.
  const [displayedForecastToDelete, setDisplayedForecastToDelete] =
    useState<ForecastToDelete | null>(null);
  // Deletes can overlap: the confirm modal closes before the request resolves, so a second row
  // can start deleting while the first is in flight.
  const [deletingForecastIds, setDeletingForecastIds] = useState<Set<ForecastId>>(new Set());

  const openDeleteDialog = useCallback((toDelete: ForecastToDelete) => {
    setForecastToDelete(toDelete);
    setDisplayedForecastToDelete(toDelete);
  }, []);
  const cancelDeleteForecast = () => setForecastToDelete(null);

  const confirmDeleteForecast = async () => {
    if (!forecastToDelete) return;
    const { forecastId } = forecastToDelete;
    setDeletingForecastIds(prev => new Set(prev).add(forecastId));
    try {
      await onDeleteForecast(forecastId);
    } catch (e) {
      setError({ message: `Couldn't delete forecast: ${e.message}`, reportError: e });
    }
    setDeletingForecastIds(prev => {
      const next = new Set(prev);
      next.delete(forecastId);
      return next;
    });
  };

  // initial measurement of doubleTableWidth, ensuring DOM is ready
  useLayoutEffect(() => {
    if (doubleTableRef.current) {
      setDoubleTableWidth(doubleTableRef.current.offsetWidth);
    }
  }, [doubleTableRef]);

  // resize listener of doubleTableWidth, side effect doesn't block painting
  useEffect(() => {
    const measureWidthOnResize = () => {
      if (doubleTableRef.current) {
        setDoubleTableWidth(doubleTableRef.current.offsetWidth);
      }
    };

    window.addEventListener('resize', measureWidthOnResize);
    return () => {
      window.removeEventListener('resize', measureWidthOnResize);
    };
  }, [doubleTableRef]);

  // 15%/266px floor: the left table now only holds Forecast Dates + Controls,
  // since KPI Volume/Revenue KPI moved to the right table.
  useEffect(() => {
    if (doubleTableWidth) {
      setLeftTableWidth(Math.max(doubleTableWidth * 0.15, 266));
    }
  }, [doubleTableWidth]);

  const rightTableWidth = useMemo(
    () => (doubleTableWidth ?? 0) - (leftTableWidth ?? 0),
    [doubleTableWidth, leftTableWidth]
  );

  // Left table's Controls column will grow to fill additional space,
  // but maintain a minimum width of LG_COL_WIDTH
  const leftTableGrowingColWidth = useMemo(
    () => Math.max((leftTableWidth ?? 0) - MED_COL_WIDTH - OUTER_X_PADDING, SM_COL_WIDTH),
    [leftTableWidth]
  );

  // Comparison Budgets and Last Modified columns will both grow to fill additional space,
  // but maintain a minimum width of MED_COL_WIDTH.
  const rightTableGrowingColWidth = useMemo(
    () =>
      Math.max(
        (rightTableWidth -
          SM_COL_WIDTH * 4 -
          MED_COL_WIDTH * 3 -
          OUTER_X_PADDING -
          INNER_X_PADDING) /
          2,
        MED_COL_WIDTH
      ),
    [rightTableWidth]
  );

  const rightData: Omit<RightData, 'groupForecastIds'>[] = useMemo(() => {
    if (!forecastGroups) {
      return [];
    }

    // Each forecast shows its own kpis, falling back to the group's combined
    // kpis for responses that predate the per-forecast field.
    const rows: ForecastRow[] = forecastGroups.flatMap(({ forecasts, ...remainingForecastVals }) =>
      forecasts.map(forecast => ({
        ...remainingForecastVals,
        ...forecast,
        kpis: forecast.kpis ?? remainingForecastVals.kpis
      }))
    );

    return rows.map(
      ({
        groupId,
        startDate,
        endDate,
        kpis,
        status,
        id,
        channel,
        platform,
        tactic,
        primaryBudget,
        comparisonBudgets,
        lastModifiedUser,
        lastModifiedDate,
        isClientFacing
      }) => {
        const volumeKpis =
          kpis
            .filter(kpi => kpi.type === 'Volume')
            .map(kpi => kpi.name)
            .join(', ') || EMPTY_KPI_TEXT;
        const revenueKpis =
          kpis
            .filter(kpi => kpi.type === 'Revenue')
            .map(kpi => kpi.name)
            .join(', ') || EMPTY_KPI_TEXT;
        const comparisonBudgetsStr = comparisonBudgets
          .map(budget => formatRawBudget(budget))
          .join(', ');

        return {
          groupId,
          startDate: dateToMddyy(startDate),
          endDate: dateToMddyy(endDate),
          volumeKpis,
          revenueKpis,
          status,
          forecastId: id,
          channel,
          platform: pooledPlatformLabel(platform),
          tactic: pooledTacticLabel(tactic),
          primaryBudget: formatRawBudget(primaryBudget),
          comparisonBudgets: comparisonBudgetsStr,
          lastModifiedUser: lastModifiedUser.fullName,
          lastModifiedDate,
          isClientFacing
        };
      }
    );
  }, [forecastGroups]);

  const filteredRightData: RightData[] = useMemo(() => {
    const filtered = filter ? rightData.filter(row => filter.filter(row)) : rightData;

    return filtered.map(row => {
      const { groupId } = row;
      const groupMates = filtered.filter(r => r.groupId === groupId);
      return { ...row, groupForecastIds: groupMates.map(r => r.forecastId) };
    });
  }, [filter, rightData]);

  const filteredLeftData: LeftData[] = useMemo(() => {
    const groups = filteredRightData.reduce<Record<string, RightData[]>>((acc, row) => {
      (acc[row.groupId] ??= []).push(row);
      return acc;
    }, {});

    return Object.values(groups).map(group => {
      const { groupId, startDate, endDate, status } = group[0];
      return {
        groupId,
        startDate,
        endDate,
        status,
        groupForecastIds: group.map(row => row.forecastId)
      };
    });
  }, [filteredRightData]);

  const leftHeaders: TypedHeader<LeftData>[] = useMemo(
    () => [
      {
        label: 'Forecast Dates',
        name: 'dateRange',
        renderer: ({ startDate, endDate }) => (
          <div className='saved-forecasts-date-range'>{joinDateRange(startDate, endDate)}</div>
        ),
        nonInteractive: true,
        width: MED_COL_WIDTH + OUTER_X_PADDING
      },
      {
        label: '',
        name: 'controls',
        renderer: row => (
          <div className='saved-forecasts-controls'>
            <div className='saved-forecasts-controls-left'>
              <StatusChip status={row.status} />
            </div>
            {showWIPFeatures && (
              <div className='saved-forecasts-controls-right'>
                <IconButton
                  appearance={Appearance.EMPTY}
                  design={Design.NEUTRAL}
                  onClick={() => {
                    console.warn('TODO: Duplicate row', row);
                    setError({ message: 'Duplicate not yet impemented' });
                  }}
                >
                  <MdContentCopy />
                </IconButton>
              </div>
            )}
          </div>
        ),
        nonInteractive: true,
        width: leftTableGrowingColWidth
      }
    ],
    [leftTableGrowingColWidth, setError, showWIPFeatures]
  );

  const rightHeaders: TypedHeader<RightData>[] = useMemo(
    () => [
      {
        label: 'Channel',
        name: 'channel',
        renderer: ({ channel, forecastId, groupForecastIds }) => (
          <div
            className={cn('saved-forecasts-channel', {
              'last-in-group': isLastVisible({ forecastId, groupForecastIds })
            })}
          >
            {channel}
          </div>
        ),
        nonInteractive: true,
        width: SM_COL_WIDTH + INNER_X_PADDING
      },
      {
        label: 'Platform',
        name: 'platform',
        renderer: ({ platform, forecastId, groupForecastIds }) => (
          <div
            className={cn('saved-forecasts-platform', {
              'last-in-group': isLastVisible({ forecastId, groupForecastIds }),
              empty: platform === ALL_PLATFORMS_LABEL
            })}
          >
            {platform}
          </div>
        ),
        nonInteractive: true,
        width: SM_COL_WIDTH
      },
      {
        label: 'Tactic',
        name: 'tactic',
        renderer: ({ tactic, forecastId, groupForecastIds }) => (
          <div
            className={cn('saved-forecasts-tactic', {
              'last-in-group': isLastVisible({ forecastId, groupForecastIds }),
              empty: tactic === ALL_TACTICS_LABEL
            })}
          >
            {tactic}
          </div>
        ),
        nonInteractive: true,
        width: SM_COL_WIDTH
      },
      {
        label: 'Volume KPI',
        name: 'volumeKPIs',
        renderer: ({ volumeKpis, forecastId, groupForecastIds }) => (
          <div
            className={cn('saved-forecasts-volume-kpis', {
              'last-in-group': isLastVisible({ forecastId, groupForecastIds }),
              empty: volumeKpis === EMPTY_KPI_TEXT
            })}
          >
            {volumeKpis}
          </div>
        ),
        nonInteractive: true,
        width: MED_COL_WIDTH
      },
      {
        label: 'Revenue KPI',
        name: 'revenueKPIs',
        renderer: ({ revenueKpis, forecastId, groupForecastIds }) => (
          <div
            className={cn('saved-forecasts-revenue-kpis', {
              'last-in-group': isLastVisible({ forecastId, groupForecastIds }),
              empty: revenueKpis === EMPTY_KPI_TEXT
            })}
          >
            {revenueKpis}
          </div>
        ),
        nonInteractive: true,
        width: MED_COL_WIDTH
      },
      {
        label: 'Primary Budget',
        name: 'primaryBudget',
        renderer: ({ primaryBudget, forecastId, groupForecastIds }) => (
          <div
            className={cn('saved-forecasts-primary-budget', {
              'last-in-group': isLastVisible({ forecastId, groupForecastIds })
            })}
          >
            {primaryBudget}
          </div>
        ),
        nonInteractive: true,
        width: MED_COL_WIDTH
      },
      {
        label: 'Comparison Budgets',
        name: 'comparisonBudgets',
        renderer: ({ comparisonBudgets, forecastId, groupForecastIds }) => (
          <div
            className={cn('saved-forecasts-comparison-budgets', {
              'last-in-group': isLastVisible({ forecastId, groupForecastIds })
            })}
          >
            {comparisonBudgets}
          </div>
        ),
        nonInteractive: true,
        width: rightTableGrowingColWidth
      },
      {
        label: 'Last Modified',
        name: 'lastModified',
        renderer: ({ lastModifiedDate, lastModifiedUser, forecastId, groupForecastIds }) => (
          <div
            className={cn('saved-forecasts-last-modified', {
              'last-in-group': isLastVisible({ forecastId, groupForecastIds })
            })}
          >
            By {lastModifiedUser} on {lastModifiedDate}
          </div>
        ),
        nonInteractive: true,
        width: rightTableGrowingColWidth
      },
      {
        label: '',
        name: 'controls',
        renderer: ({
          isClientFacing,
          forecastId,
          startDate,
          endDate,
          lastModifiedDate,
          groupForecastIds,
          status
        }) => (
          <div
            className={cn('saved-forecasts-controls', {
              'last-in-group': isLastVisible({ forecastId, groupForecastIds })
            })}
          >
            {showWIPFeatures && (
              <div className='saved-forecasts-controls-left'>
                <ToggleSwitch
                  label=''
                  checked={isClientFacing}
                  onChange={() =>
                    setError({ message: 'Updating client facing not yet supported.' })
                  }
                  labelPosition='none'
                  disabled={status !== 'Completed'}
                />
              </div>
            )}
            <div className='saved-forecasts-controls-right'>
              {status === 'Completed' && (
                <IconButton
                  appearance={Appearance.EMPTY}
                  onClick={() => {
                    goToTab(PageTab.FORECAST_RESULTS, { forecastId });
                  }}
                >
                  <MdOpenInNew />
                </IconButton>
              )}
              <IconButton
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                loading={deletingForecastIds.has(forecastId)}
                disabled={!canDeleteForecasts}
                tooltip={
                  canDeleteForecasts
                    ? undefined
                    : { text: 'Test companies are protected and cannot be deleted' }
                }
                data-testid={`delete-forecast-${forecastId}`}
                onClick={() =>
                  openDeleteDialog({ forecastId, startDate, endDate, lastModifiedDate })
                }
              >
                <MdDelete />
              </IconButton>
              {showWIPFeatures && (
                <IconButton
                  appearance={Appearance.EMPTY}
                  design={Design.NEUTRAL}
                  onClick={() =>
                    setError({
                      message: 'TODO: Display more options menu'
                    })
                  }
                >
                  <MdMoreVert />
                </IconButton>
              )}
            </div>
          </div>
        ),
        nonInteractive: true,
        width: SM_COL_WIDTH + OUTER_X_PADDING
      }
    ],
    [
      canDeleteForecasts,
      deletingForecastIds,
      goToTab,
      openDeleteDialog,
      rightTableGrowingColWidth,
      setError,
      showWIPFeatures
    ]
  );

  const resizeFrame: MouseEventHandler<HTMLDivElement> = e => {
    if (resizeActive) {
      const leftNavWidth = 60;
      const adjustedWidth = e.clientX - leftNavWidth - 28;
      setLeftTableWidth(adjustedWidth);
    }
  };

  let tableContent: ReactElement;
  if (!forecastGroups) {
    tableContent = <FullPageSpinner />;
  } else if (forecastGroups.length === 0) {
    tableContent = <div className='saved-forecasts-empty'>No forecasts to show</div>;
  } else {
    tableContent = (
      <>
        <div
          className='saved-forecasts-table-container left-table-container'
          style={{ width: leftTableWidth ?? 'auto' }}
        >
          <BPMTable
            data={filteredLeftData}
            headers={leftHeaders}
            headerHeight={32}
            superHeaders={leftSuperHeaders}
            superHeaderHeight={64}
            superHeadersRenderer={superHeadersRenderer}
            noRowsRenderer={noRowsRenderer}
            rowHeight={leftDataIndex => {
              const data = filteredLeftData[leftDataIndex];
              return data.groupForecastIds.length * BASE_ROW_HEIGHT;
            }}
            filterBar={false}
            alternateColors={false}
          />
        </div>
        <div className='resize-container'>
          <div
            role='slider'
            aria-label='resize tables'
            aria-valuenow={leftTableWidth ?? 0}
            tabIndex={0}
            className='resize-area'
            style={{ left: `${(leftTableWidth ?? 0) + 3}px` }}
            onMouseDown={() => setResizeActive(true)}
            onMouseMove={e => resizeFrame(e)}
          >
            <div className='table-divider' />
          </div>
        </div>
        <div
          className='saved-forecasts-table-container right-table-container'
          style={{ width: (doubleTableWidth ?? 0) - (leftTableWidth ?? 0) || 'auto' }}
        >
          <BPMTable
            data={filteredRightData}
            headers={rightHeaders}
            headerHeight={32}
            superHeaders={rightSuperHeaders}
            superHeaderHeight={64}
            superHeadersRenderer={superHeadersRenderer}
            noRowsRenderer={noRowsRenderer}
            rowHeight={BASE_ROW_HEIGHT}
            filterBar={false}
            alternateColors={false}
          />
        </div>
      </>
    );
  }

  return (
    <div
      role='presentation'
      className={cn('saved-forecasts-container', { 'drag-active': resizeActive })}
      data-testid='saved-forecasts'
      onMouseUp={() => setResizeActive(false)}
    >
      <TableWidget
        twHeaderTopLeft={
          <FilterBar
            size={Size.LG}
            hasAdvanced
            width={FILTER_BAR_WIDTH}
            onFilter={f => {
              setFilter({ filter: f });
            }}
            lines={rightData}
            options={filterBarOptions}
          />
        }
        twHeaderTopRight={
          <>
            {showWIPFeatures && (
              <SegmentedControlButton
                size={Size.LG}
                className='forecasts-budget-display-toggle'
                design={Design.NEUTRAL}
                options={[
                  { key: 'showPercentage', label: 'Show % Budgets' },
                  { key: 'showDollars', label: 'Show $ Budgets' }
                ]}
                selectedOption={budgetView}
                onChange={option => setBudgetView(option)}
              />
            )}
            {showWIPFeatures && canAddExistingForecast && (
              <Button
                size={Size.LG}
                leadingElement={<MdAdd />}
                onClick={() => {
                  // [DEV-18594] TODO
                  setError({ message: 'Requesting new forecasts not yet supported' });
                }}
              >
                New Forecast
              </Button>
            )}
          </>
        }
        tableComponent={
          <div className='saved-forecasts-double-table' ref={doubleTableRef}>
            {tableContent}
          </div>
        }
      />
      <AlertDialog
        intent={Intent.ERROR}
        show={Boolean(forecastToDelete)}
        onHide={cancelDeleteForecast}
        icon={<MdDelete size={95} />}
        title='Delete Forecast?'
        body={
          displayedForecastToDelete && (
            <>
              Are you sure you want to delete the forecast for{' '}
              <strong data-testid='delete-forecast-summary'>
                {joinDateRange(
                  displayedForecastToDelete.startDate,
                  displayedForecastToDelete.endDate
                )}{' '}
                &middot; {displayedForecastToDelete.forecastId.slice(0, 8)} &middot;{' '}
                {displayedForecastToDelete.lastModifiedDate}
              </strong>
              ? This action cannot be undone.
            </>
          )
        }
        primaryAction={{ onClick: confirmDeleteForecast, label: 'Delete' }}
        secondaryAction={{ onClick: cancelDeleteForecast }}
        dataTestId='delete-forecast-dialog'
        passthroughProps={{ onExited: () => setDisplayedForecastToDelete(null) }}
      />
    </div>
  );
};
